import { useCallback, useEffect, useRef, useState } from "react";
import {
  FirmSize,
  EmploymentType,
  WorkExperience,
} from "../../../__generated__/types";
import { Stack, Button, Typography } from "@mui/material";
import { DropdownSelect } from "../../../components/dropdown-select";
import { HdgCheckboxGroup } from "../../../components/hdg-checkbox-group";
import { HdgDatePicker } from "../../../components/hdg-date-picker";
import { HdgTextInput } from "../../../components/hdg-text-input";
import {
  EmploymentTypeOptions,
  FirmSizeOptions,
  isDateBeforeDate,
  isDateBeforeToday,
} from "../../../utils";
import PlusIcon from "@mui/icons-material/Add";
import { useUserContext } from "../../../hooks/userContext";

export type WorkExperienceValidations = {
  title: false | string;
  company: false | string;
  startDate: false | string;
  endDate: false | string;
  current: false | string;
  firmSize: false | string;
  employmentType: false | string;
};

export const WorkExperienceFormComponent = ({
  onChange,
  value,
  currentForm,
  setCurrentForm,
  currentFormIndex,
}: {
  value: WorkExperience[];
  onChange: (value: WorkExperience[]) => void;
  currentForm?: WorkExperience | null;
  setCurrentForm?: (value: WorkExperience | null) => void;
  currentFormIndex: number | undefined;
}) => {
  const [formInstance, setFormInstance] = useState<number>(0);

  const [title, setTitle] = useState(currentForm?.title ?? "");
  const [company, setCompany] = useState(currentForm?.company ?? "");
  const [startDate, setStartDate] = useState<string | null>(
    currentForm?.startDate ?? null
  );
  const [endDate, setEndDate] = useState<string | null>(
    currentForm?.endDate ?? null
  );
  const [current, setCurrent] = useState(
    currentForm?.current !== undefined ? currentForm?.current : false
  );
  const [firmSize, setFirmSize] = useState<FirmSize | undefined>(
    currentForm?.firmSize ?? undefined
  );
  const [employmentType, setEmploymentType] = useState<
    EmploymentType | undefined
  >(currentForm?.employmentType ?? undefined);

  const { user } = useUserContext();
  const isExistingExperience =
    !!currentForm?.id && value.some((experience) => experience.id === currentForm.id);

  const isValid =
    title &&
    company &&
    startDate &&
    (current || endDate) &&
    (current || isDateBeforeDate(startDate, endDate)) &&
    (current || isDateBeforeToday(endDate)) &&
    isDateBeforeToday(startDate) &&
    firmSize &&
    employmentType;

  const validations: WorkExperienceValidations = {
    title: !title ? "Title is required" : false,
    company: !company ? "Company is required" : false,
    startDate: !startDate
      ? "Start date is required"
      : !isDateBeforeToday(startDate)
      ? "Start date must be before today"
      : false,
    endDate:
      !endDate && !current
        ? "End date is required"
        : !current && !isDateBeforeDate(startDate, endDate)
        ? "End date must be after start date"
        : !current && !isDateBeforeToday(endDate)
        ? "End date must be before today"
        : false,
    current: false,
    firmSize: !firmSize ? "Firm size is required" : false,
    employmentType: !employmentType ? "Employment type is required" : false,
  };

  const resetForm = useCallback(() => {
    setTitle("");
    setCompany("");
    setStartDate(null);
    setEndDate(null);
    setCurrent(false);
    setFirmSize(undefined);
    setEmploymentType(undefined);
    setFormInstance((prev) => prev + 1);
  }, []);

  const hasSetCurrentExperience = useRef(false);
  useEffect(() => {
    if (currentFormIndex !== 0) return;
    if (!user || hasSetCurrentExperience.current) return;
    hasSetCurrentExperience.current = true;
    if (user?.businessName) {
      setCurrent(true);
      setCompany(user.businessName);
    }
  }, [user, value, currentFormIndex]);

  const handleSave = () => {
    if (isExistingExperience && currentForm?.id) {
      const updatedValue = value.map((experience) =>
        experience.id === currentForm.id
          ? {
              ...experience,
              title,
              company,
              startDate: startDate as string,
              endDate: endDate as string,
              current,
              firmSize: firmSize as FirmSize,
              employmentType: employmentType as EmploymentType,
            }
          : experience
      );
      onChange(updatedValue);
      resetForm();
      setCurrentForm && setCurrentForm(null);
    } else {
      onChange([
        ...value,
        {
          id: Date.now().toString(),
          title,
          company,
          startDate: startDate as string,
          endDate: endDate as string,
          current,
          firmSize: firmSize as FirmSize,
          employmentType: employmentType as EmploymentType,
        },
      ]);
      resetForm();
      setCurrentForm && setCurrentForm(null);
    }
  };

  const validationMessage = (validation: string | false) => {
    if (typeof validation === "string") return validation;
    return undefined;
  };

  const keyByInstance = (key: string) => `${key}-${formInstance}`;

  return (
    <Stack direction="column" gap={2} style={{ marginBottom: "18px" }}>
      <Stack direction={{ xs: "column", md: "row" }} gap={2}>
        <HdgTextInput
          key={keyByInstance("title")}
          label="Title/Role"
          isValid={!!!validations?.title}
          value={title}
          onChange={setTitle}
          validationText={validationMessage(validations?.title)}
        />
        <HdgTextInput
          key={keyByInstance("company")}
          label="Company"
          isValid={!!!validations?.company}
          value={company}
          onChange={setCompany}
          validationText={validationMessage(validations?.company)}
        />
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} gap={2}>
        <HdgDatePicker
          key={keyByInstance("startDate")}
          label="Start Date"
          isValid={!!!validations?.startDate}
          value={startDate}
          onChange={setStartDate}
          validationText={validationMessage(validations?.startDate)}
        />
        <HdgDatePicker
          key={keyByInstance("endDate")}
          label="End Date"
          value={endDate}
          isValid={!!!validations?.endDate}
          onChange={setEndDate}
          disabled={current ?? false}
          validationText={validationMessage(validations?.endDate)}
        />
      </Stack>
      <HdgCheckboxGroup
        key={keyByInstance("current")}
        value={current ? ["current"] : []}
        options={[
          { value: "current", label: "I am currently working in this role" },
        ]}
        onChange={(values) => setCurrent(values.includes("current"))}
      />
      <Stack direction="row" gap={2}>
        <DropdownSelect<EmploymentType>
          key={keyByInstance("employmentType")}
          label="Employment Type"
          value={employmentType}
          onChange={setEmploymentType}
          options={EmploymentTypeOptions}
          isValid={!!!validations?.employmentType}
          validationText={validationMessage(validations?.employmentType)}
        />
        <DropdownSelect<FirmSize>
          key={keyByInstance("firmSize")}
          label="Firm Size"
          value={firmSize}
          onChange={setFirmSize}
          options={FirmSizeOptions}
          isValid={!!!validations?.firmSize}
          validationText={validationMessage(validations?.firmSize)}
        />
      </Stack>
      <Button onClick={handleSave} variant="outlined" disabled={!isValid} sx={{ mb: 2 }}>
        <PlusIcon />
        <Typography variant="body2" component="span" fontWeight={600}>
          {currentForm ? "Save Experience" : "Add Experience"}
        </Typography>
      </Button>
    </Stack>
  );
};
